import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createCategory,
  selectCreateCategoryError,
  selectCreateCategoryStatus,
} from "../features/categories/categoriesSlice";

export default function CategoryCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const saveStatus = useSelector(selectCreateCategoryStatus);
  const saveError = useSelector(selectCreateCategoryError);

  const [form, setForm] = useState({ name: "", description: "" });

  const canSave = useMemo(() => saveStatus !== "saving", [saveStatus]);

  const onSave = async () => {
    const body = { name: String(form.name || "").trim(), description: String(form.description || "") };
    const res = await dispatch(createCategory({ body }));
    if (createCategory.fulfilled.match(res)) {
      const id = res.payload?.id;
      const from = location.state?.from;
      if (id != null) navigate(`/warehouse/categories/${id}/edit`, { state: { from } });
      else if (typeof from === "string" && from.startsWith("/")) navigate(from);
      else navigate("/warehouse/categories");
    }
  };

  useEffect(() => {
    // UX: focus could be added later; keep simple
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Добавить категорию</div>
          <div style={styles.subTitle}>
            Создание категории (данные из <span style={styles.mono}>POST /api/product/categories/</span>).
          </div>
        </div>
        <div style={styles.headerActions}>
          <button
            type="button"
            onClick={() => {
              const from = location.state?.from;
              if (typeof from === "string" && from.startsWith("/")) {
                navigate(from);
                return;
              }
              if (window.history.length > 1) {
                navigate(-1);
                return;
              }
              navigate("/warehouse/categories");
            }}
            style={styles.btnGhost}
          >
            ← Назад
          </button>
          <button type="button" onClick={onSave} style={styles.btnPrimary} disabled={!canSave}>
            {saveStatus === "saving" ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>

      {saveStatus === "failed" && <div style={styles.error}>{String(saveError || "Failed to save")}</div>}

      <div style={styles.body}>
        <div style={styles.card}>
          <div style={styles.formGrid}>
            <div style={styles.formField}>
              <div style={styles.label}>Name</div>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                style={styles.input}
                placeholder="Category name"
              />
            </div>
            <div style={styles.formFieldWide}>
              <div style={styles.label}>Description</div>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                style={styles.textarea}
                placeholder="Description"
                rows={6}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  mono: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
  },
  header: {
    padding: "16px 18px",
    borderBottom: "1px solid var(--c-border)",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
  },
  title: { fontSize: 20, fontWeight: 900 },
  subTitle: { marginTop: 6, opacity: 0.75, fontSize: 14, lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 10, alignItems: "center" },
  btnPrimary: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid rgba(245,197,24,0.3)",
    background: "var(--c-btn)",
    color: "var(--c-btn-text)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  btnGhost: {
    padding: "10px 12px",
    borderRadius: 99,
    border: "1px solid var(--c-border)",
    background: "transparent",
    color: "var(--c-text-sub)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 15,
  },
  error: {
    padding: "12px 18px",
    fontSize: 15,
    background: "var(--c-danger-dim)",
    color: "var(--c-danger)",
    borderTop: "1px solid var(--c-danger)",
    borderBottom: "1px solid var(--c-danger)",
  },
  body: { padding: 18, minHeight: 0, overflow: "auto" },
  card: {
    border: "1px solid var(--c-border)",
    background: "var(--c-input)",
    borderRadius: 16,
    padding: 14,
  },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  formField: { display: "grid", gap: 6 },
  formFieldWide: { display: "grid", gap: 6, gridColumn: "1 / -1" },
  label: { fontSize: 13, color: "var(--c-text-dim)", fontWeight: 900 },
  input: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
  },
  textarea: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid var(--c-border)",
    background: "var(--c-card)",
    color: "var(--c-text)",
    outline: "none",
    fontSize: 15,
    resize: "vertical",
  },
};

